import { ReactNode } from 'react';
import { makeStyles, tokens, mergeClasses } from '@fluentui/react-components';

/**
 * Clean Minimal Table Styles (Variation 1)
 * - Simple, clean design with subtle hover effects
 * - Light borders, uppercase headers, pill-style status badges
 * - Best for data-dense views where readability is key
 */
const useStyles = makeStyles({
  tableWrapper: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: tokens.fontSizeBase300,
  },
  thead: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  th: {
    textAlign: 'left' as const,
    padding: '14px 16px',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    whiteSpace: 'nowrap',
  },
  thSortable: {
    cursor: 'pointer',
    userSelect: 'none',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },
  td: {
    padding: '16px',
    fontSize: tokens.fontSizeBase300,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    verticalAlign: 'middle',
  },
  tr: {
    transition: 'background-color 0.15s ease',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  trClickable: {
    cursor: 'pointer',
  },
  trLast: {
    '& td': {
      borderBottom: 'none',
    },
  },
  // Cell content styles
  cellPrimary: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  cellTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  cellTitleClickable: {
    cursor: 'pointer',
    ':hover': {
      color: tokens.colorBrandForeground1,
      textDecoration: 'underline',
    },
  },
  cellSubtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  cellMono: {
    fontFamily: "'Consolas', 'Monaco', monospace",
    fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground4,
    padding: '4px 10px',
    borderRadius: tokens.borderRadiusSmall,
    display: 'inline-block',
  },
  cellDate: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  cellActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
  },
  // Status badge styles
  status: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
  },
  statusDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: 'currentColor',
  },
  statusSuccess: {
    backgroundColor: '#e6f4ea',
    color: '#1e7e34',
  },
  statusPending: {
    backgroundColor: '#fff3cd',
    color: '#856404',
  },
  statusFailed: {
    backgroundColor: '#f8d7da',
    color: '#c82333',
  },
  statusInProgress: {
    backgroundColor: '#cce5ff',
    color: '#004085',
  },
  statusActive: {
    backgroundColor: '#e6f4ea',
    color: '#1e7e34',
  },
  statusInactive: {
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
  },
  statusTrial: {
    backgroundColor: '#eff6ff',
    color: '#1d4ed8',
  },
  statusNew: {
    backgroundColor: '#e0f2fe',
    color: '#0369a1',
  },
  statusUpgrade: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
  },
  statusRollback: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
  },
  // Action buttons
  actionButton: {
    padding: '6px 12px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase200,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  actionButtonPrimary: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
  // Empty state
  emptyRow: {
    textAlign: 'center' as const,
    color: tokens.colorNeutralForeground3,
    padding: '48px 16px',
  },
});

// Export styles for use in components
export const useDataTableStyles = useStyles;

// Type definitions
export interface DataTableColumn<T> {
  key: string;
  header: string;
  width?: string;
  sortable?: boolean;
  render: (item: T, index: number) => ReactNode;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  keyExtractor: (item: T) => string;
  onRowClick?: (item: T) => void;
  emptyMessage?: string;
  className?: string;
}

export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  emptyMessage = 'No data available',
  className,
}: DataTableProps<T>) {
  const styles = useStyles();

  return (
    <div className={mergeClasses(styles.tableWrapper, className)}>
      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={mergeClasses(
                  styles.th,
                  column.sortable && styles.thSortable
                )}
                style={column.width ? { width: column.width } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className={styles.emptyRow}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((item, index) => (
              <tr
                key={keyExtractor(item)}
                className={mergeClasses(
                  styles.tr,
                  onRowClick && styles.trClickable,
                  index === data.length - 1 && styles.trLast
                )}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
              >
                {columns.map((column) => (
                  <td key={column.key} className={styles.td}>
                    {column.render(item, index)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// Status Badge Component
export type StatusType =
  | 'success'
  | 'pending'
  | 'failed'
  | 'in-progress'
  | 'active'
  | 'inactive'
  | 'trial'
  | 'new'
  | 'upgrade'
  | 'rollback';

interface TableStatusBadgeProps {
  status: string;
  showDot?: boolean;
}

const statusMap: Record<string, StatusType> = {
  Success: 'success',
  Completed: 'success',
  Active: 'active',
  Pending: 'pending',
  Failed: 'failed',
  Error: 'failed',
  InProgress: 'in-progress',
  'In Progress': 'in-progress',
  Inactive: 'inactive',
  Trial: 'trial',
  New: 'new',
  Upgrade: 'upgrade',
  Rollback: 'rollback',
};

export function TableStatusBadge({ status, showDot = true }: TableStatusBadgeProps) {
  const styles = useStyles();

  const statusType = statusMap[status] || 'pending';

  const statusStyles: Record<StatusType, string> = {
    success: styles.statusSuccess,
    pending: styles.statusPending,
    failed: styles.statusFailed,
    'in-progress': styles.statusInProgress,
    active: styles.statusActive,
    inactive: styles.statusInactive,
    trial: styles.statusTrial,
    new: styles.statusNew,
    upgrade: styles.statusUpgrade,
    rollback: styles.statusRollback,
  };

  return (
    <span className={mergeClasses(styles.status, statusStyles[statusType])}>
      {showDot && <span className={styles.statusDot} />}
      {status}
    </span>
  );
}

// Cell helper components
interface CellPrimaryProps {
  title: string;
  subtitle?: string;
  onClick?: () => void;
}

export function CellPrimary({ title, subtitle, onClick }: CellPrimaryProps) {
  const styles = useStyles();

  return (
    <div className={styles.cellPrimary}>
      <span
        className={mergeClasses(
          styles.cellTitle,
          onClick && styles.cellTitleClickable
        )}
        onClick={onClick}
      >
        {title}
      </span>
      {subtitle && <span className={styles.cellSubtitle}>{subtitle}</span>}
    </div>
  );
}

interface CellMonoProps {
  children: ReactNode;
}

export function CellMono({ children }: CellMonoProps) {
  const styles = useStyles();
  return <span className={styles.cellMono}>{children}</span>;
}

interface CellDateProps {
  date?: Date | string;
  format?: 'date' | 'datetime' | 'relative';
}

export function CellDate({ date, format = 'date' }: CellDateProps) {
  const styles = useStyles();

  if (!date) return <span className={styles.cellDate}>—</span>;

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  let formatted: string;
  if (format === 'relative') {
    formatted = formatRelativeTime(dateObj);
  } else if (format === 'datetime') {
    formatted = dateObj.toLocaleString();
  } else {
    formatted = dateObj.toLocaleDateString();
  }

  return <span className={styles.cellDate}>{formatted}</span>;
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

interface CellActionsProps {
  children: ReactNode;
}

export function CellActions({ children }: CellActionsProps) {
  const styles = useStyles();
  return <div className={styles.cellActions}>{children}</div>;
}
